import { isApplePlatform } from '../../layout/TopBar';

export type HotkeyPlatform = 'apple' | 'other';

/** One chord of a shortcut: a key plus the modifiers held with it. `mod` is ⌘ on Apple and Ctrl elsewhere. */
export interface KeyCombo {
  /** Lower-cased `KeyboardEvent.key`; `' '` for space. */
  key: string;
  mod: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

/** What a keydown looked like, in the same terms as `KeyCombo` so the two can be compared. */
export interface KeyPress {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  space: ' ',
  spacebar: ' ',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  del: 'delete',
  plus: '+',
};

const MODIFIER_KEYS = new Set(['shift', 'control', 'alt', 'meta', 'os', 'altgraph', 'capslock']);

/** Whether a keydown is a modifier on its own, which never completes a shortcut. */
export function isModifierKey(key: string): boolean {
  return MODIFIER_KEYS.has(key.toLowerCase());
}

/** The platform the shortcuts render and match for. */
export function detectHotkeyPlatform(): HotkeyPlatform {
  return isApplePlatform() ? 'apple' : 'other';
}

function parseCombo(step: string): KeyCombo {
  const combo: KeyCombo = { key: '', mod: false, ctrl: false, alt: false, shift: false, meta: false };
  const parts =
    step.endsWith('+') && step.length > 1 ? [...step.slice(0, -1).split('+').filter(Boolean), '+'] : step.split('+');
  for (const rawPart of parts) {
    const part = rawPart.trim().toLowerCase();
    if (part === '') continue;
    switch (part) {
      case 'mod':
        combo.mod = true;
        break;
      case 'ctrl':
      case 'control':
        combo.ctrl = true;
        break;
      case 'alt':
      case 'option':
        combo.alt = true;
        break;
      case 'shift':
        combo.shift = true;
        break;
      case 'meta':
      case 'cmd':
      case 'command':
      case 'super':
      case 'win':
        combo.meta = true;
        break;
      default:
        combo.key = KEY_ALIASES[part] ?? part;
    }
  }
  return combo;
}

/**
 * `'mod+k'` → one chord; `'g s'` → a two-step sequence; `'shift+/'` and `'?'` both mean the question
 * mark. Steps are separated by whitespace, modifiers by `+`.
 */
export function parseHotkeys(keys: string): KeyCombo[] {
  return keys
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(parseCombo)
    .filter((combo) => combo.key !== '');
}

/** A keydown as a `KeyPress`, or `null` when it is a modifier by itself. */
export function keyPressFromEvent(
  event: Pick<KeyboardEvent, 'key' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey'>,
): KeyPress | null {
  if (!event.key || isModifierKey(event.key)) return null;
  return {
    key: event.key === 'Spacebar' ? ' ' : event.key.toLowerCase(),
    ctrl: event.ctrlKey,
    alt: event.altKey,
    shift: event.shiftKey,
    meta: event.metaKey,
  };
}

const isLetterOrNamed = (key: string) => /^[a-z]$/.test(key) || key.length > 1;

/**
 * Whether a keypress is this chord. Modifiers must match exactly, except that Shift is not checked for
 * punctuation (`?` already implies it) and `mod` resolves per platform.
 */
export function matchesCombo(press: KeyPress, combo: KeyCombo, platform: HotkeyPlatform): boolean {
  if (press.key !== combo.key) return false;
  const wantMeta = combo.meta || (combo.mod && platform === 'apple');
  const wantCtrl = combo.ctrl || (combo.mod && platform !== 'apple');
  if (press.meta !== wantMeta || press.ctrl !== wantCtrl || press.alt !== combo.alt) return false;
  if (isLetterOrNamed(combo.key) && press.shift !== combo.shift) return false;
  return true;
}

const APPLE_KEY_LABELS: Record<string, string> = {
  escape: 'Esc',
  enter: '↩',
  ' ': 'Space',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  backspace: '⌫',
  delete: '⌦',
  tab: '⇥',
  home: 'Home',
  end: 'End',
  pageup: 'PgUp',
  pagedown: 'PgDn',
};

const OTHER_KEY_LABELS: Record<string, string> = {
  escape: 'Esc',
  enter: 'Enter',
  ' ': 'Space',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  backspace: 'Backspace',
  delete: 'Del',
  tab: 'Tab',
  home: 'Home',
  end: 'End',
  pageup: 'PgUp',
  pagedown: 'PgDn',
};

function comboLabel(combo: KeyCombo, platform: HotkeyPlatform): string {
  const apple = platform === 'apple';
  const labels = apple ? APPLE_KEY_LABELS : OTHER_KEY_LABELS;
  const key =
    labels[combo.key] ??
    (combo.key.length === 1 ? combo.key.toUpperCase() : combo.key.charAt(0).toUpperCase() + combo.key.slice(1));
  const ctrl = combo.ctrl || (combo.mod && !apple);
  const meta = combo.meta || (combo.mod && apple);
  if (apple) {
    return `${ctrl ? '⌃' : ''}${combo.alt ? '⌥' : ''}${combo.shift ? '⇧' : ''}${meta ? '⌘' : ''}${key}`;
  }
  const parts: string[] = [];
  if (ctrl) parts.push('Ctrl');
  if (meta) parts.push('Win');
  if (combo.alt) parts.push('Alt');
  if (combo.shift) parts.push('Shift');
  parts.push(key);
  return parts.join('+');
}

export interface FormatKeysOptions {
  /** Defaults to the detected platform. */
  platform?: HotkeyPlatform;
  /** The word between sequence steps. Default `then`. */
  thenLabel?: string;
}

/**
 * The display form of a shortcut: `⌘K` on Apple, `Ctrl+K` elsewhere; a sequence reads `G then S`.
 * Use `formatKeySteps` for one string per step when rendering a `<Kbd>` each.
 */
export function formatKeys(keys: string, options: FormatKeysOptions = {}): string {
  return formatKeySteps(keys, options).join(` ${options.thenLabel ?? 'then'} `);
}

/** One display string per step of the shortcut. */
export function formatKeySteps(keys: string, options: FormatKeysOptions = {}): string[] {
  const platform = options.platform ?? detectHotkeyPlatform();
  return parseHotkeys(keys).map((combo) => comboLabel(combo, platform));
}

/** Whether keystrokes on this target are the user typing, not commanding. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return true;
  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}
